import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ApiError } from "@/lib/apiClient";
import { passwordReset } from "@/features/auth/api/auth.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { PasswordField, useAutoFocus } from "@/components/form";
import { Notice } from "@/components/Notice";

const schema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters."),
    confirm: z.string().min(1, "Confirm your new password."),
  })
  .refine((v) => v.password === v.confirm, {
    message: "The passwords don't match.",
    path: ["confirm"],
  });
type ResetForm = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const { token = "" } = useParams<{ token: string }>();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const form = useForm<ResetForm>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  useAutoFocus(form, "password", valid && !checking);

  useEffect(() => {
    let cancelled = false;
    passwordReset.validate(token)
      .then((r) => {
        if (cancelled) return;
        setValid(r.valid);
        setEmail(r.email ?? null);
      })
      .catch(() => { if (!cancelled) setValid(false); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  async function onSubmit(v: ResetForm) {
    setError(null);
    try {
      await passwordReset.reset(token, v.password);
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reset the password.");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <h1 className="text-2xl font-semibold tracking-tight mb-1">Set a new password</h1>

          {checking ? (
            <p className="text-sm text-muted-foreground mt-4">Checking your link…</p>
          ) : done ? (
            <div className="mt-4 space-y-4">
              <Notice tone="success">
                Your password has been updated. You can now sign in.
              </Notice>
              <Button asChild>
                <Link to="/login">Go to sign in</Link>
              </Button>
            </div>
          ) : !valid ? (
            <div className="mt-4 space-y-4">
              <Notice tone="error">
                This reset link is invalid or has expired. Reset links are valid for
                2 hours — ask your administrator to send a new one.
              </Notice>
              <Button asChild variant="ghost">
                <Link to="/login">← Back to sign in</Link>
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-4">
                {email && (
                  <p className="text-sm text-muted-foreground">
                    Choose a new password for{" "}
                    <span className="font-medium text-foreground">{email}</span>.
                  </p>
                )}
                <PasswordField
                  control={form.control}
                  name="password"
                  label="New password"
                  autoComplete="new-password"
                />
                <PasswordField
                  control={form.control}
                  name="confirm"
                  label="Confirm new password"
                  autoComplete="new-password"
                />

                {error && <Notice tone="error">{error}</Notice>}

                <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
                  {form.formState.isSubmitting ? "Saving…" : "Set password"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
